/**
 * @file SegmentsTab.tsx
 * What: Presents Shopify StudyRun segment results as scan-friendly grouped score cards.
 * Transport / data-flow: Pure React view over the signed Shopify results payload; it does not fetch or mutate data.
 *
 * Algorithm or layout: Groups scored report segments by audience profile, then compares claims within each profile.
 */
import { useId, type ReactNode } from 'react'
import type { ShopifyResultAudience } from '@testfeed/shopify-contracts'
import { badgeTone } from '../badgeTone'
import { ArmLabelDisplay } from './ArmLabelDisplay'
import type { ShopifySelectedStudyRun, ShopifyStudyResultArmDetail } from '../../types'
import {
  shopifyComponentCopy,
  productLanguage,
  formatShopifySegmentsAudienceGroupCountLabel,
  formatShopifySegmentProfilesHeading,
} from '@testfeed/product-language'
import { formatIncomeSegmentLabel, formatUnderscoreValue } from '../incomeSegmentLabels'
import { buildArmsRows, buildShopifyTreatmentIdentityMap } from '@testfeed/shopify-contracts'
import './SegmentsTab.css'

// A segment entry with sub-fields coalesced to safe defaults. The wire contract
// tolerates nullish sub-fields in legacy report artifacts; the UI normalizes them
// at the boundary in buildSegmentEntries so downstream comparisons and progress
// bars always receive concrete numbers.
type SegmentEntry = {
  dimension: string
  segment: string
  n: number
  t2b: number
  t2bPercent: string
  ratingDistribution: number[]
  armId: string
  armLabel: string
  armDisplayLabel: string
  armOrder: number
  armImageUrl: string | null
  armTreatmentDescription: string | null
}

type AudienceGroup = {
  dimension: string
  segment: string
  entries: SegmentEntry[]
}

type GroupedSegments = Array<readonly [string, AudienceGroup[]]>

const CORE_DIMENSIONS = ['gender', 'age_band', 'income']
const MIN_CONFIDENT_SAMPLE_SIZE = 5
const MIN_USABLE_CLAIM_GAP_POINTS = 2

/** Renders scored segment performance from the merchant-facing results contract. */
export function SegmentsTab({ selectedRun }: { selectedRun: ShopifySelectedStudyRun }) {
  const arms = selectedRun.details?.arms
  if (!arms) {
    return (
      <s-section>
        <s-text color="subdued">{shopifyComponentCopy.resultsSegmentsTab.noSegmentBreakdownsAreAvailable001}</s-text>
      </s-section>
    )
  }

  const segments = buildSegmentEntries(arms, selectedRun.treatmentMode)
  const groupedSegments = groupSegmentsByDimensionAndAudience(segments).flatMap(([dimension, groups]) => {
    const confidentGroups = groups.filter(isConfidentAudiencePattern)
    if (confidentGroups.length === 0) return []
    return [[dimension, confidentGroups] as const]
  })
  const audienceGroupCount = groupedSegments.reduce((count, [, groups]) => count + groups.length, 0)
  const hasSegments = audienceGroupCount > 0

  if (!hasSegments) {
    return (
      <s-section>
        <s-text color="subdued">{shopifyComponentCopy.resultsSegmentsTab.noSegmentBreakdownsWereAvailable002}</s-text>
      </s-section>
    )
  }

  return (
    <s-stack direction="block" gap="large-100">
      <SegmentReadoutCard segmentCount={audienceGroupCount} />
      <ScoredSegmentsSection groupedSegments={groupedSegments} />
    </s-stack>
  )
}

function buildSegmentEntries(
  arms: ShopifyStudyResultArmDetail[],
  treatmentMode: ShopifySelectedStudyRun['treatmentMode'],
): SegmentEntry[] {
  const identities = buildShopifyTreatmentIdentityMap(arms, treatmentMode)
  const descriptions = new Map(
    buildArmsRows(arms, treatmentMode).map((arm) => [arm.armId, arm.treatmentDescription ?? null]),
  )
  return arms.flatMap((arm, armOrder) =>
    arm.segments.map((segment) => ({
      // Coalesce nullish sub-fields to safe defaults. The wire contract tolerates
      // missing sub-fields in legacy report artifacts (the assembler's truthy guards
      // check object existence, not sub-field existence). At runtime JavaScript's
      // loose semantics already produce NaN/false for comparisons against undefined;
      // coalescing to 0 makes that existing degraded behavior explicit and type-safe.
      n: segment.n ?? 0,
      t2b: segment.t2b ?? 0,
      t2bPercent: segment.t2bPercent ?? '0%',
      ratingDistribution: segment.ratingDistribution ?? [],
      dimension: segment.dimension,
      segment: segment.segment,
      armId: arm.armId,
      armLabel: arm.label,
      armDisplayLabel: identities.get(arm.armId) ?? productLanguage.studyResults.tables.treatmentRole,
      armOrder,
      armImageUrl: arm.imageUrl ?? null,
      armTreatmentDescription: descriptions.get(arm.armId) ?? null,
    })),
  )
}

function groupSegmentsByDimensionAndAudience(segments: SegmentEntry[]) {
  const grouped = new Map<string, Map<string, AudienceGroup>>()
  for (const segment of segments) {
    const dimensionGroups = grouped.get(segment.dimension) ?? new Map<string, AudienceGroup>()
    const audienceGroup = dimensionGroups.get(segment.segment) ?? {
      dimension: segment.dimension,
      segment: segment.segment,
      entries: [],
    }
    audienceGroup.entries.push(segment)
    dimensionGroups.set(segment.segment, audienceGroup)
    grouped.set(segment.dimension, dimensionGroups)
  }

  return [...grouped.entries()]
    .map(([dimension, dimensionGroups]) => {
      const audienceGroups = [...dimensionGroups.values()].map((group) => ({
        ...group,
        entries: group.entries.toSorted((left, right) => left.armOrder - right.armOrder),
      }))

      return [dimension, audienceGroups.toSorted(compareAudienceGroups)] as const
    })
    .toSorted(([left], [right]) => {
      const leftIndex = CORE_DIMENSIONS.indexOf(left)
      const rightIndex = CORE_DIMENSIONS.indexOf(right)
      const normalizedLeftIndex = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex
      const normalizedRightIndex = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex
      if (normalizedLeftIndex !== normalizedRightIndex) return normalizedLeftIndex - normalizedRightIndex
      return left.localeCompare(right)
    })
}

function compareAudienceGroups(left: AudienceGroup, right: AudienceGroup) {
  const leftBestShare = maxLikelyBuyerShare(left.entries)
  const rightBestShare = maxLikelyBuyerShare(right.entries)
  if (leftBestShare !== rightBestShare) return rightBestShare - leftBestShare
  return formatSegmentLabel(left.segment, left.dimension).localeCompare(
    formatSegmentLabel(right.segment, right.dimension),
  )
}

function maxLikelyBuyerShare(entries: SegmentEntry[]) {
  return entries.reduce((max, entry) => Math.max(max, entry.t2b), 0)
}

function SegmentReadoutCard({ segmentCount }: { segmentCount: number }) {
  return (
    <s-section>
      <s-stack direction="block" gap="small-300">
        <s-stack direction="inline" justifyContent="space-between" alignItems="start" gap="small-300">
          <s-stack direction="block" gap="small-100">
            <s-heading>{shopifyComponentCopy.resultsSegmentsTab.audiencePatterns003}</s-heading>
            <s-text color="subdued">{shopifyComponentCopy.resultsSegmentsTab.showsWhichClaimsFitEach004}</s-text>
            <s-text color="subdued">{shopifyComponentCopy.resultsSegmentsTab.likelyBuyerShareHelp008}</s-text>
          </s-stack>
          <s-badge tone={badgeTone('info')}>{formatShopifySegmentsAudienceGroupCountLabel(segmentCount)}</s-badge>
        </s-stack>
      </s-stack>
    </s-section>
  )
}

function ScoredSegmentsSection({ groupedSegments }: { groupedSegments: GroupedSegments }) {
  const audienceGroups = groupedSegments.flatMap(([, groups]) => groups)

  return (
    <s-stack direction="block" gap="small-300">
      <s-heading>{shopifyComponentCopy.resultsSegmentsTab.audienceGroups005}</s-heading>
      <AudienceGroupGrid>
        {audienceGroups.map((group) => (
          <AudienceGroupCard key={`${group.dimension}:${group.segment}`} group={group} />
        ))}
      </AudienceGroupGrid>
    </s-stack>
  )
}

function AudienceGroupCard({ group }: { group: AudienceGroup }) {
  return (
    <ResultAudienceGroup
      group={{
        id: `${group.dimension}:${group.segment}`,
        title: formatShopifySegmentProfilesHeading(formatSegmentLabel(group.segment, group.dimension)),
        dimension: formatDimension(group.dimension),
        rows: group.entries.map((segment) => ({
          armId: segment.armId,
          armLabel: segment.armDisplayLabel,
          imageUrl: segment.armImageUrl,
          description: segment.armTreatmentDescription,
          value: segment.t2bPercent,
          progress: percentProgress(segment.t2b),
          accessibilityLabel: `${segment.armDisplayLabel}: ${segment.t2bPercent}`,
        })),
      }}
      rowLabel={shopifyComponentCopy.resultsSegmentsTab.message006}
      metricLabel={shopifyComponentCopy.resultsSegmentsTab.likelyBuyerShare007}
    />
  )
}

/** Original audience grid and group cards; no grouping, filtering, sorting or score conversion. */
export function AudiencePatternsView({ data, intro }: { data: ShopifyResultAudience; intro: ReactNode }) {
  return (
    <s-stack direction="block" gap="large-100">
      {intro}
      <s-stack direction="block" gap="small-300">
        <s-heading>{data.heading}</s-heading>
        <AudienceGroupGrid>
          {data.groups.map((group) => (
            <ResultAudienceGroup key={group.id} group={group} rowLabel={data.rowLabel} metricLabel={data.metricLabel} />
          ))}
        </AudienceGroupGrid>
      </s-stack>
    </s-stack>
  )
}
function ResultAudienceGroup({
  group,
  rowLabel,
  metricLabel,
}: {
  group: ShopifyResultAudience['groups'][number]
  rowLabel: string
  metricLabel: string
}) {
  return (
    <div data-testid={`audience-group-card-${group.id}`}>
      <s-section>
        <s-stack direction="block" gap="small-300">
          <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="small-200">
            <s-text type="strong">{group.title}</s-text>
            <s-badge>{group.dimension}</s-badge>
          </s-stack>
          <s-stack direction="block" gap="small-200">
            <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="small-300">
              <s-text color="subdued">{rowLabel}</s-text>
              <s-text color="subdued">{metricLabel}</s-text>
            </s-stack>
            {group.rows.map((row) => (
              <VariantSegmentRow key={row.armId} row={row} />
            ))}
          </s-stack>
        </s-stack>
      </s-section>
    </div>
  )
}
function VariantSegmentRow({ row }: { row: ShopifyResultAudience['groups'][number]['rows'][number] }) {
  const labelId = useId()
  const valueId = useId()
  return (
    <s-box border="base" borderRadius="small" padding="small-200">
      <s-stack direction="block" gap="small-200">
        <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="small-300">
          <div className="tf-segment-message">
            <s-stack direction="inline" gap="small-200" alignItems="center">
              <div className="tf-segment-thumbnail-slot" data-testid="segment-thumbnail-slot">
                <ArmLabelDisplay label={row.imageUrl} size="small" />
              </div>
              <s-stack direction="block" gap="small-100">
                <s-text type="strong" id={labelId}>
                  {row.armLabel}
                </s-text>
                {row.description && <s-text color="subdued">{row.description}</s-text>}
              </s-stack>
            </s-stack>
          </div>
          <div className="tf-segment-score-slot" data-testid="segment-score-slot">
            <s-text type="strong" id={valueId}>
              {row.value}
            </s-text>
          </div>
        </s-stack>
        <s-progress value={row.progress} max={100} accessibilityLabel={row.accessibilityLabel} />
      </s-stack>
    </s-box>
  )
}

function isConfidentAudiencePattern(group: AudienceGroup) {
  if (group.entries.length < 2) return false
  if (!group.entries.every((entry) => entry.n >= MIN_CONFIDENT_SAMPLE_SIZE)) return false
  return likelyBuyerShareGapPoints(group.entries) > MIN_USABLE_CLAIM_GAP_POINTS
}

function likelyBuyerShareGapPoints(entries: SegmentEntry[]) {
  const sortedShares = entries.map((entry) => percentProgress(entry.t2b)).toSorted((left, right) => right - left)
  return (sortedShares[0] ?? 0) - (sortedShares[1] ?? 0)
}

function formatDimension(value: string) {
  if (value === 'age_band') return 'Age band'
  return formatUnderscoreValue(value)
}

function formatSegmentLabel(value: string, dimension: string) {
  if (dimension === 'income') return formatIncomeSegmentLabel(value)
  return formatUnderscoreValue(value)
}

function percentProgress(value: number) {
  return Math.min(100, Math.max(0, Math.round(value * 100)))
}

/** Container queries need an explicit query container; avoid commas inside responsive track values. */
function AudienceGroupGrid({ children }: { children: ReactNode }) {
  return (
    <div data-testid="audience-group-grid">
      <s-query-container>
        <s-grid gridTemplateColumns="@container (inline-size > 480px) 1fr 1fr, 1fr" gap="base">
          {children}
        </s-grid>
      </s-query-container>
    </div>
  )
}
